export type AppRole =
  | "guest"
  | "customer"
  | "provider_owner"
  | "stylist"
  | "hnb_staff";

export type ProviderOnboardingStep =
  | "start"
  | "profile"
  | "verification"
  | "services"
  | "calendar"
  | "deposit-policy"
  | "portfolio"
  | "review"
  | "complete";

export const HNB_STAFF_ROLE_VALUES = [
  "CEO",
  "MDCEO",
  "BUSINESS_HEAD",
  "BDO",
  "CTO",
  "ERP_HEAD",
  "LLM_HEAD",
  "IT_COMPLIANCE_HEAD",
  "DATA_ENGINEER",
  "DEVELOPER",
  "DATA_SCIENTIST",
  "IT_COMPLIANCE_OFFICER",
  "CFO",
  "ACCOUNTS_HEAD",
  "ACCOUNTS_OFFICER",
  "FINANCE_HEAD",
  "FINANCE_OFFICER",
  "HR_HEAD",
  "HR_MANAGER",
  "AUDIT_HEAD",
  "AUDITOR",
  "GRIEVANCE_HEAD",
  "GRIEVANCE_OFFICER",
  "OPERATIONS",
  "SUPPORT",
] as const;

export type StaffSubRole = (typeof HNB_STAFF_ROLE_VALUES)[number];

export type StaffScopeType =
  | "global"
  | "department"
  | "assigned_area"
  | "assigned_task";

export type StaffPermission =
  | "dashboard.view"
  | "dashboard.executive"
  | "department.analytics"
  | "records.view.department"
  | "records.view.assigned"
  | "tasks.view.assigned"
  | "tasks.manage.assigned"
  | "approvals.view"
  | "approvals.manage"
  | "team.view"
  | "team.manage"
  | "reports.view"
  | "reports.export"
  | "settings.view"
  | "settings.manage"
  | "audit.view"
  | "audit.manage"
  | "finance.view"
  | "finance.manage"
  | "grievance.view"
  | "grievance.manage"
  | "hr.view"
  | "hr.manage"
  | "operations.view"
  | "operations.manage"
  | "it.view"
  | "it.manage";

export type StaffRolePolicy = {
  dashboardPath: string;
  scopeType: StaffScopeType;
  permissions: StaffPermission[];
};

export const PROVIDER_ONBOARDING_STEPS: ProviderOnboardingStep[] = [
  "start",
  "profile",
  "verification",
  "services",
  "calendar",
  "deposit-policy",
  "portfolio",
  "review",
  "complete",
];

const STAFF_SUB_ROLES = new Set<string>([
  "ceo",
  "mdceo",
  "business_head",
  "businesshead",
  "bdo",
  "cto",
  "erp_head",
  "erphead",
  "llm_head",
  "llmhead",
  "it_compliance_head",
  "itcompliancehead",
  "data_engineer",
  "dataengineer",
  "developer",
  "data_scientist",
  "datascientist",
  "it_compliance_officer",
  "itcomplianceofficer",
  "cfo",
  "accounts_head",
  "accountshead",
  "accounts_officer",
  "accountsofficer",
  "finance_head",
  "financehead",
  "finance_officer",
  "financeofficer",
  "hr_head",
  "hrhead",
  "hr_manager",
  "hrmanager",
  "audit_head",
  "audithead",
  "auditor",
  "grievance_head",
  "grievancehead",
  "grievance_officer",
  "grievanceofficer",
  "operations",
  "support",
  "hnb_staff",
  "hnbstaff",
]);

export const STAFF_ROLE_POLICY: Record<StaffSubRole, StaffRolePolicy> = {
  CEO: {
    dashboardPath: "/staff/dashboard/md",
    scopeType: "global",
    permissions: [
      "dashboard.view",
      "dashboard.executive",
      "department.analytics",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "team.view",
      "reports.view",
      "reports.export",
      "settings.view",
    ],
  },

  MDCEO: {
    dashboardPath: "/staff/dashboard/md",
    scopeType: "global",
    permissions: [
      "dashboard.view",
      "dashboard.executive",
      "department.analytics",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "team.view",
      "reports.view",
      "reports.export",
      "settings.view",
    ],
  },

  BUSINESS_HEAD: {
    dashboardPath: "/staff/dashboard/operations",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "operations.view",
      "operations.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "team.view",
      "reports.view",
      "reports.export",
    ],
  },

  BDO: {
    dashboardPath: "/staff/dashboard/operations",
    scopeType: "assigned_area",
    permissions: [
      "dashboard.view",
      "operations.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "team.view",
      "reports.view",
    ],
  },

  OPERATIONS: {
    dashboardPath: "/staff/dashboard/operations",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "operations.view",
      "operations.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "reports.export",
    ],
  },

  CTO: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "global",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "it.view",
      "it.manage",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "reports.export",
      "settings.view",
      "settings.manage",
    ],
  },

  ERP_HEAD: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "it.view",
      "it.manage",
      "records.view.department",
      "approvals.view",
      "team.view",
      "reports.view",
    ],
  },

  LLM_HEAD: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "it.view",
      "it.manage",
      "records.view.department",
      "reports.view",
    ],
  },

  IT_COMPLIANCE_HEAD: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "it.view",
      "audit.view",
      "records.view.department",
      "approvals.view",
      "reports.view",
    ],
  },

  DATA_ENGINEER: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "it.view",
      "it.manage",
      "records.view.department",
      "team.view",
      "reports.view",
      "reports.export",
    ],
  },

  DEVELOPER: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "it.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "reports.view",
    ],
  },

  DATA_SCIENTIST: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "it.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "reports.view",
    ],
  },

  IT_COMPLIANCE_OFFICER: {
    dashboardPath: "/staff/dashboard/it",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "audit.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "reports.view",
    ],
  },

  CFO: {
    dashboardPath: "/staff/dashboard/cfo",
    scopeType: "global",
    permissions: [
      "dashboard.view",
      "dashboard.executive",
      "finance.view",
      "finance.manage",
      "department.analytics",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "reports.export",
      "settings.view",
    ],
  },

  ACCOUNTS_HEAD: {
    dashboardPath: "/staff/dashboard/accounts",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "finance.view",
      "finance.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "reports.export",
      "team.view",
    ],
  },

  ACCOUNTS_OFFICER: {
    dashboardPath: "/staff/dashboard/accounts",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "finance.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "reports.view",
    ],
  },

  FINANCE_HEAD: {
    dashboardPath: "/staff/dashboard/finance",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "finance.view",
      "finance.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "reports.export",
      "team.view",
    ],
  },

  FINANCE_OFFICER: {
    dashboardPath: "/staff/dashboard/finance",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "finance.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "reports.view",
    ],
  },

  HR_HEAD: {
    dashboardPath: "/staff/dashboard/hr",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "hr.view",
      "hr.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "team.view",
      "team.manage",
      "reports.view",
      "reports.export",
    ],
  },

  HR_MANAGER: {
    dashboardPath: "/staff/dashboard/hr",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "hr.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "team.view",
      "reports.view",
    ],
  },

  AUDIT_HEAD: {
    dashboardPath: "/staff/dashboard/audit",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "audit.view",
      "audit.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "reports.export",
      "team.view",
    ],
  },

  AUDITOR: {
    dashboardPath: "/staff/dashboard/audit",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "audit.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
      "reports.view",
    ],
  },

  GRIEVANCE_HEAD: {
    dashboardPath: "/staff/dashboard/grievance",
    scopeType: "department",
    permissions: [
      "dashboard.view",
      "department.analytics",
      "grievance.view",
      "grievance.manage",
      "records.view.department",
      "approvals.view",
      "approvals.manage",
      "reports.view",
      "team.view",
    ],
  },

  GRIEVANCE_OFFICER: {
    dashboardPath: "/staff/dashboard/grievance",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "grievance.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
    ],
  },

  SUPPORT: {
    dashboardPath: "/staff/dashboard/grievance",
    scopeType: "assigned_task",
    permissions: [
      "dashboard.view",
      "grievance.view",
      "records.view.assigned",
      "tasks.view.assigned",
      "tasks.manage.assigned",
    ],
  },
};

function toUpperSnakeCase(value: string): string {
  return value
    .trim()
    .replace(/[\s-]+/g, "_")
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toUpperCase();
}

export function normalizeRole(value: unknown): AppRole {
  if (typeof value !== "string") return "guest";

  const normalized = value.trim().toLowerCase();

  if (normalized === "customer" || normalized === "stylist") {
    return normalized as AppRole;
  }

  if (
    normalized === "provider_owner" ||
    normalized === "provider-owner" ||
    normalized === "providerowner" ||
    normalized === "provider" ||
    normalized === "service_provider" ||
    normalized === "service-provider" ||
    normalized === "serviceprovider" ||
    normalized === "spowner" ||
    normalized === "sp_owner" ||
    normalized === "service_provider_owner" ||
    normalized === "service-provider-owner"
  ) {
    return "provider_owner";
  }

  if (STAFF_SUB_ROLES.has(normalized)) {
    return "hnb_staff";
  }

  if (
    normalized === "hnb_staff" ||
    normalized === "hnbstaff" ||
    normalized === "hnb staff"
  ) {
    return "hnb_staff";
  }

  return "guest";
}

export function normalizeStaffSubRole(value: unknown): StaffSubRole | null {
  if (typeof value !== "string") return null;

  const normalized = toUpperSnakeCase(value);

  return HNB_STAFF_ROLE_VALUES.includes(normalized as StaffSubRole)
    ? (normalized as StaffSubRole)
    : null;
}

export function normalizeOnboardingStep(
  value: unknown,
): ProviderOnboardingStep | undefined {
  if (typeof value !== "string") return undefined;

  return PROVIDER_ONBOARDING_STEPS.includes(
    value as ProviderOnboardingStep,
  )
    ? (value as ProviderOnboardingStep)
    : undefined;
}

export function getStaffDashboardPath(
  staffSubRole: StaffSubRole | null | undefined,
): string {
  if (!staffSubRole) return "/staff/dashboard/overview";

  return (
    STAFF_ROLE_POLICY[staffSubRole]?.dashboardPath ??
    "/staff/dashboard/overview"
  );
}

export function getStaffScopeType(
  staffSubRole: StaffSubRole | null | undefined,
): StaffScopeType | null {
  if (!staffSubRole) return null;
  return STAFF_ROLE_POLICY[staffSubRole]?.scopeType ?? null;
}

export function getStaffPermissions(
  staffSubRole: StaffSubRole | null | undefined,
): StaffPermission[] {
  if (!staffSubRole) return [];
  return STAFF_ROLE_POLICY[staffSubRole]?.permissions ?? [];
}

export function hasStaffPermission(
  staffSubRole: StaffSubRole | null | undefined,
  permission: StaffPermission,
): boolean {
  return getStaffPermissions(staffSubRole).includes(permission);
}

export function getPostLoginRouteByRole(
  role: AppRole,
  staffSubRole?: StaffSubRole | null,
): string {
  switch (role) {
    case "provider_owner":
      return "/provider/entry";
    case "hnb_staff":
      return getStaffDashboardPath(staffSubRole);
    case "customer":
      return "/customer/dashboard";
    case "stylist":
      return "/stylist/dashboard";
    default:
      return "/";
  }
}